import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import { Loader2, Send } from "lucide-react";
import WhatsAppAssociaDialog from "./WhatsAppAssociaDialog";

const API = process.env.REACT_APP_BACKEND_URL;

export default function WhatsAppConversazione({ conversationKey, headers, onChanged }) {
  const [data, setData] = useState(null);
  const [text, setText] = useState("");
  const [associaOpen, setAssociaOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);

  const load = async () => {
    if (!conversationKey) return;
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/admin/crm/whatsapp/conversazioni/${encodeURIComponent(conversationKey)}`, { headers });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.detail || "Conversazione non disponibile");
      setData(json);
    } catch (error) { toast.error(error.message); } finally { setLoading(false); }
  };

  useEffect(() => {
    load();
    const timer = setInterval(load, 20000);
    return () => clearInterval(timer);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversationKey]);

  const send = async () => {
    if (!text.trim() || !conversationKey) return;
    setSending(true);
    try {
      const res = await fetch(`${API}/api/admin/crm/whatsapp/conversazioni/${encodeURIComponent(conversationKey)}/messaggi`, {
        method: "POST", headers, body: JSON.stringify({ text: text.trim() }),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.detail || "Messaggio non inviato");
      setText("");
      load();
    } catch (error) { toast.error(error.message); } finally { setSending(false); }
  };

  const link = data?.conversation?.collegamenti;

  if (!conversationKey) return <p className="p-6 text-sm text-slate-500">Seleziona una conversazione.</p>;
  if (loading && !data) return <p className="p-6 text-sm text-slate-500"><Loader2 className="mr-2 inline h-4 w-4 animate-spin" />Caricamento…</p>;
  return <div className="flex min-h-[420px] flex-col rounded border">
    <div className="border-b p-3 text-sm"><strong>{data?.conversation?.number}</strong><span className="ml-2 text-slate-500">{data?.window_24h_open ? "Finestra 24 ore aperta" : "Finestra 24 ore chiusa"}</span></div>
    {data?.conversation && (link?.riconosciuta
      ? <div className="flex flex-wrap items-start justify-between gap-2 border-b bg-emerald-50 p-3 text-sm">
        <div>
          <p><span className="text-slate-500">Riconosciuto come:</span> <strong>{link.etichetta}</strong>{link.struttura?.comune ? ` · ${link.struttura.comune}` : ""}</p>
          {link.via_testo && <p className="text-xs text-slate-600">{link.via_testo}</p>}
        </div>
        <Button size="sm" variant="outline" onClick={() => setAssociaOpen(true)}>Cambia associazione</Button>
      </div>
      : <div className="flex flex-wrap items-start justify-between gap-2 border-b bg-amber-50 p-3 text-sm">
        <div>
          <p className="font-medium text-amber-900">Numero non riconosciuto</p>
          <p className="text-xs text-amber-900">Non è tra i numeri delle strutture, dei lead o dei familiari degli ospiti. Associalo per leggere le risposte delle strutture e ritrovare la persona.</p>
        </div>
        <Button size="sm" onClick={() => setAssociaOpen(true)}>Associa a struttura, lead o ospite</Button>
      </div>)}
    <div className="flex-1 space-y-2 overflow-y-auto p-3">
      {(data?.messages || []).map((message) => <div key={message.id || message.meta_message_id} className={`max-w-[80%] rounded px-3 py-2 text-sm ${message.direction === "outbound" ? "ml-auto bg-emerald-100" : "bg-slate-100"}`}>
        <p className="whitespace-pre-wrap">{message.text || (message.template ? `Template: ${message.template}` : "Documento inviato")}</p>
        <p className="mt-1 text-[10px] text-slate-500">{message.created_at ? new Date(message.created_at).toLocaleString("it-IT") : ""} · {message.status || ""}</p>
        {message.status === "failed" && <p className="mt-1 text-xs text-rose-700">{message.errore || "Meta non ha consegnato il messaggio."}</p>}
      </div>)}
    </div>
    {data?.window_24h_open ? <div className="flex gap-2 border-t p-3"><Input value={text} onChange={(event) => setText(event.target.value)} onKeyDown={(event) => event.key === "Enter" && send()} placeholder="Scrivi un messaggio" /><Button onClick={send} disabled={sending}><Send className="h-4 w-4" /></Button></div>
      : <p className="border-t p-3 text-xs text-slate-500">La risposta libera si apre solo dopo un messaggio in arrivo da questo numero. Se dal telefono hai già risposto e qui non compare, Meta non sta ancora consegnando i messaggi ad Affidya.</p>}
    <WhatsAppAssociaDialog open={associaOpen} onOpenChange={setAssociaOpen} conversationKey={conversationKey} numero={data?.conversation?.number} headers={headers} onDone={() => { load(); onChanged?.(); window.dispatchEvent(new Event("affidya-commerciale-menu-counts")); }} />
  </div>;
}
